/**
 * ErrorBoundary - Catches React rendering errors
 * 
 * Prevents white screens by showing a friendly fallback UI.
 * Use around routes or major components.
 */
import React from 'react';
import { AlertTriangle, RefreshCw, Copy, CheckCheck } from 'lucide-react';

class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null,
            copied: false
        };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
        this.setState({ errorInfo });

        // Log to console for debugging
        console.error('ErrorBoundary caught an error:', error, errorInfo);

        // Could send to error tracking service here
        // e.g., Sentry.captureException(error, { extra: errorInfo });
    }

    handleReload = () => {
        // Clear cache and reload
        if ('caches' in window) {
            caches.keys().then(names => {
                names.forEach(name => {
                    caches.delete(name);
                });
            });
        }
        window.location.reload();
    };

    handleCopyError = () => {
        const { error, errorInfo } = this.state;
        const errorText = `
Error: ${error?.toString()}

Stack: ${error?.stack || 'No stack trace'}

Component Stack: ${errorInfo?.componentStack || 'No component stack'}

URL: ${window.location.href}
Time: ${new Date().toISOString()}
User Agent: ${navigator.userAgent}
        `.trim();

        navigator.clipboard.writeText(errorText).then(() => {
            this.setState({ copied: true });
            setTimeout(() => this.setState({ copied: false }), 2000);
        });
    };

    render() {
        if (this.state.hasError) {
            const { fallback } = this.props;

            // Custom fallback if provided
            if (fallback) {
                return fallback;
            }

            // Default fallback UI
            return (
                <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-6">
                    <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-8 text-center">
                        {/* Icon */}
                        <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-amber-100 flex items-center justify-center">
                            <AlertTriangle size={32} className="text-amber-600" />
                        </div>

                        {/* Title */}
                        <h1 className="text-2xl font-bold text-slate-800 mb-2">
                            Something went wrong
                        </h1>

                        <p className="text-slate-600 mb-6">
                            Don't worry, your data is safe. Try refreshing the page.
                        </p>

                        {/* Actions */}
                        <div className="flex flex-col gap-3">
                            <button
                                onClick={this.handleReload}
                                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors"
                            >
                                <RefreshCw size={18} />
                                Reload Page
                            </button>

                            <button
                                onClick={this.handleCopyError}
                                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-slate-100 text-slate-700 rounded-xl font-medium hover:bg-slate-200 transition-colors"
                            >
                                {this.state.copied ? (
                                    <>
                                        <CheckCheck size={18} className="text-emerald-600" />
                                        Copied!
                                    </>
                                ) : (
                                    <>
                                        <Copy size={18} />
                                        Copy Error Details
                                    </>
                                )}
                            </button>
                        </div>

                        {/* Error preview (collapsed) */}
                        {this.state.error && (
                            <details className="mt-6 text-left">
                                <summary className="text-sm text-slate-500 cursor-pointer hover:text-slate-700">
                                    Technical details
                                </summary>
                                <pre className="mt-2 p-3 bg-slate-100 rounded-lg text-xs text-red-600 overflow-auto max-h-32">
                                    {this.state.error.toString()}
                                </pre>
                            </details>
                        )}
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

/**
 * ChunkLoadErrorBoundary - Specifically handles dynamic import failures
 * Shows "Update available" message for stale chunks
 */
export class ChunkLoadErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasChunkError: false };
    }

    static getDerivedStateFromError(error) {
        // Check if it's a chunk load error
        if (
            error.name === 'ChunkLoadError' ||
            error.message?.includes('Loading chunk') ||
            error.message?.includes('Failed to fetch dynamically imported module')
        ) {
            return { hasChunkError: true };
        }
        // Let non-chunk errors propagate to the parent ErrorBoundary
        return null;
    }

    handleRefresh = () => {
        // Force hard refresh
        window.location.reload(true);
    };

    render() {
        if (this.state.hasChunkError) {
            return (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl shadow-xl p-6 max-w-sm text-center">
                        <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-blue-100 flex items-center justify-center">
                            <RefreshCw size={24} className="text-blue-600" />
                        </div>
                        <h2 className="text-lg font-semibold text-slate-800 mb-1">
                            Update Available
                        </h2>
                        <p className="text-sm text-slate-600 mb-4">
                            A new version is available. Please refresh to get the latest features.
                        </p>
                        <button
                            onClick={this.handleRefresh}
                            className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
                        >
                            Refresh Now
                        </button>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
